/**
 * Cairn's motion vocabulary.
 *
 * Every animated thing in the app picks one of these, so the whole interface
 * moves with the same physics. Before this, transitions were specified where
 * they were used — springs at 420, 500 and 700, tweens at 0.16, 0.18, 0.2, 0.4
 * and 0.45 — and the inconsistency is what read as unfinished: two panels
 * opening side by side settled at different rates.
 *
 * Springs, not durations, for anything a finger starts. A spring carries the
 * gesture's momentum into the animation, so the panel feels attached to the
 * hand rather than replayed from a timeline. Durations are kept for opacity,
 * where there is no sense of weight to honour.
 *
 * The numbers are damping ratios just under 1: settled in a single approach,
 * no visible bounce. Bounce reads as toy-like on something people use to file
 * medical bills.
 */

export const transitions = {
  /** Sheets, dialogs, anything large arriving from an edge. */
  sheet: { type: 'spring', stiffness: 340, damping: 34, mass: 0.9 },

  /** Popovers and menus — small, near the finger, should feel immediate. */
  pop: { type: 'spring', stiffness: 480, damping: 34, mass: 0.7 },

  /** Switches and segmented controls: the snappiest thing in the app. */
  control: { type: 'spring', stiffness: 620, damping: 40, mass: 0.6 },

  /** Cross-fades that should not draw attention to themselves. */
  fade: { duration: 0.2, ease: [0.16, 1, 0.3, 1] },

  /** Content arriving once data has loaded. Slower: it is new information. */
  enter: { duration: 0.36, ease: [0.16, 1, 0.3, 1] },
} as const;

/**
 * A list whose items arrive one after another rather than all at once.
 *
 * Capped deliberately: past about six items the stagger stops reading as
 * craft and starts reading as lag, so later rows all land together.
 */
export function stagger(index: number, step = 0.035, cap = 6) {
  return Math.min(index, cap) * step;
}
